import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../auth/supabase';
import '../inventory/inventory.css';
import '../inventory/member-admin.css';

function when(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function remsStatus(member) {
  if (member.disabled) return 'REMS · Revoked';
  if (member.member_kind === 'rems_permanent') return 'REMS · Permanent';
  if (member.member_kind === 'rems_temporary') {
    const expiry = Date.parse(member.temporary_access_expires_at || '');
    if (!Number.isFinite(expiry) || expiry <= Date.now()) return 'REMS · Temporary Expired';
    return 'REMS · Pending Verification';
  }
  return 'REMS';
}

export default function RemsAccessAdmin() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setError('');
    const { data, error: rpcError } = await supabase.rpc('admin_list_rems_members');
    if (rpcError) {
      setError(rpcError.message || 'Unable to load REMS access records.');
      setLoading(false);
      return;
    }
    setMembers(Array.isArray(data) ? data : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    temporary: members.filter(item => item.member_kind === 'rems_temporary' && !item.disabled).length,
    permanent: members.filter(item => item.member_kind === 'rems_permanent' && !item.disabled).length,
    revoked: members.filter(item => item.disabled).length,
  }), [members]);

  const changeAccess = async (member, newAccess) => {
    const actionText = newAccess === 'permanent'
      ? 'Verify this REMS member and make access permanent?'
      : newAccess === 'temporary'
        ? 'Renew this REMS member for another 30 days as temporary access?'
        : 'Revoke this REMS member now? New Door Access credentials will be denied immediately.';
    if (!window.confirm(`${actionText}\n\n${member.full_name || member.email}`)) return;

    setWorkingId(member.id);
    setError('');
    setMessage('');
    const { data, error: rpcError } = await supabase.rpc('admin_set_rems_access', {
      target_user_id: member.id,
      new_access: newAccess,
    });
    setWorkingId('');
    if (rpcError) {
      setError(rpcError.message || 'Unable to change REMS access.');
      return;
    }
    if (data?.id) setMembers(current => current.map(item => item.id === data.id ? { ...item, ...data } : item));
    setMessage(
      newAccess === 'permanent'
        ? `${member.full_name || member.email} is now a verified permanent REMS member.`
        : newAccess === 'temporary'
          ? `${member.full_name || member.email} has a new 30-day temporary REMS window.`
          : `${member.full_name || member.email} has been revoked.`,
    );
    await load();
  };

  return (
    <section className="inventory-card inventory-member-admin" style={{ marginTop: 18 }}>
      <div className="inventory-section-head">
        <div>
          <h2>REMS Quick Access</h2>
          <p>Individual SMS-verified REMS identities. Temporary accounts expire after 30 days unless you verify them as permanent.</p>
        </div>
        <button type="button" onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      <div className="inventory-member-summary">
        <div><small>Pending Verification</small><strong>{counts.temporary}</strong></div>
        <div><small>Permanent</small><strong>{counts.permanent}</strong></div>
        <div><small>Revoked</small><strong>{counts.revoked}</strong></div>
      </div>

      {error && <div className="inventory-error">{error}</div>}
      {message && <div className="inventory-member-success">{message}</div>}
      {loading && <div className="inventory-empty">Loading REMS access…</div>}
      {!loading && !members.length && <div className="inventory-empty">No REMS Quick Access accounts yet.</div>}

      {!loading && members.map(member => {
        const temporary = member.member_kind === 'rems_temporary';
        const permanent = member.member_kind === 'rems_permanent';
        const expired = temporary && (!member.temporary_access_expires_at || Date.parse(member.temporary_access_expires_at) <= Date.now());
        return (
          <div className="inventory-member-row" key={member.id}>
            <div className="inventory-member-main">
              <div className="inventory-member-name-line">
                <strong>{member.full_name || 'REMS member'}</strong>
                <span className={`inventory-member-pill ${member.disabled || expired ? 'disabled' : permanent ? 'approved' : 'pending'}`}>{remsStatus(member)}</span>
              </div>
              <span>{member.email}</span>
              {temporary && <small>Temporary access expires: {when(member.temporary_access_expires_at)} · Last seen: {when(member.last_seen_at)}</small>}
              {permanent && <small>Admin verified: {when(member.rems_admin_verified_at)} · Last seen: {when(member.last_seen_at)}</small>}
            </div>
            <div className="inventory-row-actions">
              {!member.disabled && temporary && <button type="button" className="inventory-save" disabled={workingId === member.id} onClick={() => changeAccess(member, 'permanent')}>{workingId === member.id ? 'Saving…' : 'Verify & Make Permanent'}</button>}
              {(member.disabled || expired) && <button type="button" className="inventory-save" disabled={workingId === member.id} onClick={() => changeAccess(member, 'temporary')}>{workingId === member.id ? 'Saving…' : 'Renew 30 Days'}</button>}
              {!member.disabled && <button type="button" className="inventory-danger-action" disabled={workingId === member.id} onClick={() => changeAccess(member, 'disabled')}>Revoke</button>}
            </div>
          </div>
        );
      })}
    </section>
  );
}
